import { useState, useEffect, useMemo, useCallback } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../firebaseConfig";
import { useFavorites } from "../Components/Context/FavoritesContext";
import {
  PAGE_SIZE,
  normalizeText,
  getCityOptions,
  getNeighborhoodOptions,
  sortProperties,
  applySearchCriteria,
} from "../utils/propertyListingUtils";

const MODALITY_VALUE = {
  comprar: "Venda",
  alugar: "Aluguel",
};

function readPersistedState(pageKey) {
  try {
    const raw = sessionStorage.getItem(`listing-state-${pageKey}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Busca imoveisPublicos/ (sempre o conjunto completo — rawAll), aplica a
 * regra de negócio da página (pageFilter), opcionalmente a finalidade
 * (Todos/Comprar/Alugar, só quando enableModality), depois os filtros de
 * cidade/bairro escolhidos pelo usuário, ordena e pagina visualmente.
 *
 * Quando mergeFilterResults é true (hoje só TodosImoveis) e existe uma
 * pesquisa REAL aplicada no FilterModal (searchApplied === true, setado
 * apenas quando o usuário clica em "Mostrar imóveis"), os critérios dessa
 * pesquisa — exceto modalidade — são reaplicados sobre o conjunto completo
 * via applySearchCriteria, preservando cidade/categoria/quartos/etc mesmo
 * que o usuário troque a finalidade depois. Isso evita tanto mostrar dados
 * "contaminados" por um cálculo interno do FilterModal que roda no mount
 * (sem o usuário ter pesquisado nada) quanto descartar uma pesquisa real ao
 * trocar de finalidade.
 *
 * Filtros e visibleCount são persistidos por página em sessionStorage para
 * sobreviver a lista -> detalhe -> voltar.
 */
export default function usePropertyListing({
  pageKey,
  pageFilter,
  mergeFilterResults = false,
  enableModality = false,
  // Modalidade inicial quando nada foi persistido ainda (ex.: /pesquisa
  // quer nascer já na modalidade da busca aplicada no FilterModal).
  defaultModality,
  // Quando true, a modalidade não é exposta como controle interativo da
  // página (nenhum <PropertyFilters showModality>): não conta para
  // hasActiveFilters e "Limpar filtros" não mexe nela — só quem decide a
  // modalidade nesse caso é a pesquisa original.
  lockModality = false,
}) {
  const { filters, searchApplied } = useFavorites();
  const [rawAll, setRawAll] = useState(null);

  const persisted = useMemo(() => readPersistedState(pageKey), [pageKey]);

  const [modality, setModalityState] = useState(
    enableModality ? persisted?.modality || defaultModality || "todos" : "todos"
  );
  const [city, setCityState] = useState(persisted?.city || "");
  const [neighborhood, setNeighborhoodState] = useState(
    persisted?.neighborhood || ""
  );
  const [sort, setSortState] = useState(persisted?.sort || "relevancia");
  const [visibleCount, setVisibleCount] = useState(
    persisted?.visibleCount || PAGE_SIZE
  );

  // Quando a modalidade é travada (ex.: /pesquisa), não há controle de UI
  // que chame setModality — quem decide é a pesquisa aplicada no
  // FilterModal. Como a navegação de "Editar busca" reaproveita a mesma
  // rota (sem remount), o useState inicial de `modality` não é reavaliado
  // quando defaultModality muda; este efeito resincroniza o estado interno
  // sempre que uma nova pesquisa trouxer uma modalidade diferente.
  useEffect(() => {
    if (!lockModality || !defaultModality) return;
    setModalityState((current) => {
      if (current === defaultModality) return current;
      setVisibleCount(PAGE_SIZE);
      return defaultModality;
    });
  }, [lockModality, defaultModality]);

  useEffect(() => {
    const imoveisRef = ref(db, "imoveisPublicos/");
    const unsubscribe = onValue(imoveisRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setRawAll([]);
        return;
      }

      const list = Object.entries(data)
        .map(([id, imovel]) => ({ id, ...imovel }))
        .filter((imovel) => imovel.imovelInfo?.status === true)
        .reverse();

      setRawAll(list);
    });

    return () => unsubscribe();
  }, []);

  const hasSearchApplied =
    mergeFilterResults && searchApplied && Boolean(filters?.criteria);

  const activeRaw = useMemo(() => {
    if (!rawAll) return rawAll;
    if (!hasSearchApplied) return rawAll;
    return applySearchCriteria(rawAll, filters.criteria, modality);
  }, [rawAll, hasSearchApplied, filters?.criteria, modality]);

  const pageScoped = useMemo(
    () => (activeRaw || []).filter(pageFilter),
    [activeRaw, pageFilter]
  );

  const modalityScoped = useMemo(() => {
    if (!enableModality || modality === "todos") return pageScoped;
    const target = MODALITY_VALUE[modality];
    return pageScoped.filter((p) => p.imovelInfo?.modalidade === target);
  }, [pageScoped, enableModality, modality]);

  const cityOptions = useMemo(
    () => getCityOptions(modalityScoped),
    [modalityScoped]
  );
  const neighborhoodOptions = useMemo(
    () => getNeighborhoodOptions(modalityScoped, city),
    [modalityScoped, city]
  );

  useEffect(() => {
    if (activeRaw === null) return;
    if (
      neighborhood &&
      !neighborhoodOptions.some(
        (n) => normalizeText(n) === normalizeText(neighborhood)
      )
    ) {
      setNeighborhoodState("");
    }
  }, [activeRaw, neighborhoodOptions, neighborhood]);

  const filteredProperties = useMemo(() => {
    let list = modalityScoped;
    if (city) {
      list = list.filter(
        (p) => normalizeText(p.imovelInfo?.cidade) === normalizeText(city)
      );
    }
    if (neighborhood) {
      list = list.filter(
        (p) =>
          normalizeText(p.imovelInfo?.bairro) === normalizeText(neighborhood)
      );
    }
    return sortProperties(list, sort);
  }, [modalityScoped, city, neighborhood, sort]);

  const visibleProperties = useMemo(
    () => filteredProperties.slice(0, visibleCount),
    [filteredProperties, visibleCount]
  );

  useEffect(() => {
    try {
      sessionStorage.setItem(
        `listing-state-${pageKey}`,
        JSON.stringify({ modality, city, neighborhood, sort, visibleCount })
      );
    } catch {
      // sessionStorage indisponível (modo privado etc.) — degrada sem persistência
    }
  }, [pageKey, modality, city, neighborhood, sort, visibleCount]);

  const setModality = useCallback((value) => {
    setModalityState(value);
    setVisibleCount(PAGE_SIZE);
  }, []);

  const setCity = useCallback((value) => {
    setCityState(value);
    setVisibleCount(PAGE_SIZE);
  }, []);

  const setNeighborhood = useCallback((value) => {
    setNeighborhoodState(value);
    setVisibleCount(PAGE_SIZE);
  }, []);

  const setSort = useCallback((value) => {
    setSortState(value);
    setVisibleCount(PAGE_SIZE);
  }, []);

  const clearFilters = useCallback(() => {
    if (!lockModality) setModalityState("todos");
    setCityState("");
    setNeighborhoodState("");
    setSortState("relevancia");
    setVisibleCount(PAGE_SIZE);
  }, [lockModality]);

  const loadMore = useCallback(() => {
    setVisibleCount((count) => count + PAGE_SIZE);
  }, []);

  return {
    loading: activeRaw === null,
    totalCount: pageScoped.length,
    filteredProperties,
    visibleProperties,
    cityOptions,
    neighborhoodOptions,
    modality,
    setModality,
    city,
    neighborhood,
    sort,
    setCity,
    setNeighborhood,
    setSort,
    clearFilters,
    loadMore,
    hasActiveFilters: Boolean(
      city ||
        neighborhood ||
        (enableModality && !lockModality && modality !== "todos")
    ),
  };
}
